import React, { useState, useEffect } from "react";
import { 
  getMeusMedicamentos, 
  adicionarMedicamento, 
  deletarMedicamento,
  getMeusModelosMedicamentos 
} from "../../services/authService";
import Grid from "../Grid/Grid";
import MedicationForm from "../MedicationForm/MedicationForm";
import RelatorioPreview from "../RelatorioPreview/RelatorioPreview";
import NotificationPopup from '../../components/NoticationPopup/NotificationPopup';
import { useNotifications } from '../../context/NotificationContext';
import "./Home.scss";

const initialGrid = Array(3).fill(null).map(() => Array(3).fill(null));

const Home = () => {
  const [grid, setGrid] = useState(initialGrid);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [medicamentos, setMedicamentos] = useState([]);
  const [modelosMedicamento, setModelosMedicamento] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    emUso: 0,
    proximosFim: 0
  });
  const [notification, setNotification] = useState({ visible: false, nome: '', slot: '' });
  const { addNotification } = useNotifications();

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await getMeusMedicamentos();
        setMedicamentos(response);
        updateGrid(response);
        
        // Calcular estatísticas
        setStats({
          total: response.length,
          emUso: response.filter(m => m.status === 'ativo').length,
          proximosFim: response.filter(m => m.quantidade && m.quantidade < 5).length
        });
        
        // Carregar modelos de medicamentos
        const modelos = await getMeusModelosMedicamentos();
        setModelosMedicamento(modelos);
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      }
    };
    loadData();
  }, []);

  const updateGrid = (medicamentosList) => {
    const newGrid = initialGrid.map((row, rowIndex) =>
      row.map((_, colIndex) =>
        medicamentosList.find(m => m.linha === rowIndex && m.coluna === colIndex) || null
      )
    );
    setGrid(newGrid);
  };

  const handleSlotClick = (row, col) => {
    const medicamento = grid[row][col];
    // Se já existe um medicamento nesse slot, não permite edição
    if (medicamento) {
      return;
    }
    
    setSelectedSlot({ row, col });
    setIsPopupOpen(true);
  };

  const handleSave = async (data) => {
    try {
      // Somente adição de novos medicamentos, sem edição
      const novoMedicamento = {
        ...data,
        linha: selectedSlot.row,
        coluna: selectedSlot.col
      };
      await adicionarMedicamento(novoMedicamento);

      const updatedMedicamentos = await getMeusMedicamentos();
      setMedicamentos(updatedMedicamentos);
      updateGrid(updatedMedicamentos);
      
      // Atualizar estatísticas
      setStats({
        total: updatedMedicamentos.length,
        emUso: updatedMedicamentos.filter(m => m.status === 'ativo').length,
        proximosFim: updatedMedicamentos.filter(m => m.quantidade && m.quantidade < 5).length
      });
      
      setIsPopupOpen(false);
      agendarNotificacao(selectedSlot.row, selectedSlot.col, data.nome || novoMedicamento.nome || "Medicamento");
    } catch (error) {
      console.error("Erro ao salvar medicamento:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletarMedicamento(id);
      const updatedMedicamentos = medicamentos.filter(m => m.id !== id);
      setMedicamentos(updatedMedicamentos);
      updateGrid(updatedMedicamentos);
      
      // Atualizar estatísticas
      setStats({
        total: updatedMedicamentos.length,
        emUso: updatedMedicamentos.filter(m => m.status === 'ativo').length,
        proximosFim: updatedMedicamentos.filter(m => m.quantidade && m.quantidade < 5).length
      });

      // Adicionar notificação de medicamento removido
      addNotification({
        title: "Medicamento removido",
        message: "Um medicamento foi removido da caixa de medicamentos",
        type: "info"
      });
    } catch (error) {
      console.error("Erro ao deletar medicamento:", error);
    }
  };

  const agendarNotificacao = (linha, coluna, nome) => {
    let tempo = 0;
    if (linha === 0) tempo = 1 * 60 * 1000; // 1 minuto
    if (linha === 1) tempo = 2 * 60 * 1000; // 2 minutos
    if (linha === 2) tempo = 3 * 60 * 1000; // 3 minutos

    setTimeout(() => {
      // Exibe o popup de notificação
      setNotification({
        visible: true,
        nome: nome,
        slot: `Slot ${coluna + 1}`
      });
      
      // Adiciona à central de notificações
      addNotification({
        title: "Hora da medicação",
        message: `${nome} - Slot ${coluna + 1}`,
        type: "reminder"
      });
    }, tempo);
  };

  const handleNotificationClose = () => {
    setNotification({ ...notification, visible: false });
    
    // Adiciona notificação de medicamento tomado
    addNotification({
      title: "Medicamento tomado",
      message: `${notification.nome} foi marcado como tomado`,
      type: "success"
    });
  };

  return (
    <div className="home-wrapper fade-in">
      <h1>Painel de Medicamentos</h1>
      
      <div className="stats-container fade-in">
        <div className="info-card">
          <div>
            <span className="info-title">Total de Medicamentos</span>
            <div className="info-value">{stats.total}</div>
          </div>
          <i className="fas fa-pills"></i>
        </div>
        
        <div className="info-card">
          <div>
            <span className="info-title">Em Uso Ativo</span>
            <div className="info-value">{stats.emUso}</div>
          </div>
          <i className="fas fa-check-circle"></i>
        </div>
        
        <div className="info-card">
          <div>
            <span className="info-title">Próximos ao Fim</span>
            <div className="info-value">{stats.proximosFim}</div>
          </div>
          <i className="fas fa-exclamation-triangle"></i>
        </div>
      </div>
      
      <div className="home-content">
        <div className="grid-section fade-in">
          <h2>Caixa de Medicamentos</h2>
          <div className="grid-instructions">
            <p>Clique em um slot vazio para adicionar um medicamento usando um modelo.</p>
          </div>
          <Grid 
            grid={grid} 
            onSlotClick={handleSlotClick}
            onDelete={handleDelete}
          />
        </div>
        
        <div className="relatorio-preview-section fade-in">
          <h2>Prévia do Relatório</h2>
          <RelatorioPreview medicamentos={medicamentos} />
        </div>
      </div>
      
      {isPopupOpen && (
        <MedicationForm
          initialData={{ 
            linha: selectedSlot.row,
            coluna: selectedSlot.col
          }}
          modelos={modelosMedicamento}
          onSave={handleSave}
          onClose={() => setIsPopupOpen(false)}
        />
      )}
      
      {notification.visible && (
        <NotificationPopup
          nome={notification.nome}
          slot={notification.slot}
          onClose={handleNotificationClose}
        />
      )}
    </div>
  );
};

export default Home;